// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import _ from 'lodash';

import { palette } from 'themeV5/palette';
import type {
  Balancing,
  BalancingResponse,
} from 'commons/modules/service/balancing';

import type { BalanceStoreType } from './types/balance-store';
import type { RootState } from './configureStore';

function assignBy(key: string) {
  return (data: any, item: any) => {
    data[item[key]] = item;
    return data;
  };
}

// Filtros normales (dataQuery)
const normalQueryValues = {
  market_type: [],
  instrument: [],
  symbol: [],
  portfolio: [],
  strategy: [],
  institute: [],
  account: [],
  advisor: [],
  client: [],
};

// Filtros especiales (dataQuery)
const specialQueryValues = {
  specialOperation: '',
  virtualIssuer: 'Emisora Virtual',
  virtualPurchasePercentage: '',
  virtualPrice: '',
  virtualSalePercentage: '',
  includeValueStrategy: true,
  excludeValueStrategy: false,
};

const initialState: BalanceStoreType = {
  balancing: [],
  balancingSpecialOperation: null,
  total: 0,
  page: 1,
  pageSize: 50,
  filters: {
    client: '',
    marketType: '',
    instrument: '',
    symbol: '',
    portfolio: '',
    strategy: '',
    institute: '',
    account: '',
    advisor: '',
    includeZeroTitles: false,
  },
  operationQuery: {
    client: '',
    marketType: '',
    instrument: '',
    symbol: '',
    portfolio: '',
    strategy: '',
    institute: '',
    account: '',
    advisor: '',
    includeZeroTitles: false,
  },
  specialFilters: {
    specialOperation: '',
    virtualIssuer: 'Emisora Virtual',
    virtualPurchasePercentage: '',
    virtualPrice: '',
    virtualSalePercentage: '',
    includeValueStrategy: true,
    excludeValueStrategy: false,
  },
  specialOperationQuery: {
    specialOperation: '',
    virtualIssuer: 'Emisora Virtual',
    virtualPurchasePercentage: '',
    virtualPrice: '',
    virtualSalePercentage: '',
    includeValueStrategy: true,
    excludeValueStrategy: false,
  },
  notSelected: [],
  cleanFilters: false,
  filtersObject: false,
  showTooltip: true,
  operationType: '',
  openFilter: {
    marketType: false,
    instrument: false,
    symbol: false,
    portfolio: false,
    strategy: false,
    institute: false,
    account: false,
    advisor: false,
    client: false,
  },
  specialOpenFilter: {
    specialOperation: false,
    virtualIssuer: false,
    virtualPurchasePercentage: false,
    virtualPrice: false,
    virtualSalePercentage: false,
    includeValueStrategy: false,
    excludeValueStrategy: false,
  },
  dataQuery: normalQueryValues,
  specialDataQuery: specialQueryValues,
  filterOpened: true,
  normalizedData: {},
  complete: false,
  selected: [],
  selectedNotLiquidity: [],
  loading: false,
  isSubmitting: false,
  globalOrdersFilters: [],
};

export const slice = createSlice({
  name: 'balance',
  initialState: { ...initialState },
  reducers: {
    setBalancingSpecialOperation: (
      balance: BalanceStoreType,
      action: PayloadAction<BalancingResponse | null>,
    ) => {
      balance.balancingSpecialOperation = action.payload;
    },
    setShowFilterSidebar: (balance: BalanceStoreType) => {
      balance.filterOpened = !balance.filterOpened;
    },
    setGlobalOrdersFilters: (balance: BalanceStoreType, action) => {
      balance.globalOrdersFilters = action.payload;
    },
    setBalancingData: (
      balance: BalanceStoreType,
      action: PayloadAction<BalancingResponse>,
    ) => {
      const data: Record<string, any>[] = action.payload.data.map((row) => {
        let selectedLiquidity = false;

        if (
          balance.selectedNotLiquidity &&
          balance.selectedNotLiquidity.length
        ) {
          const itemSelected = balance.selectedNotLiquidity.find(
            (item) => item.idRebalance === row.idRebalance,
          );
          if (itemSelected) {
            selectedLiquidity = true;
          }
        }

        return {
          ...row,
          selected: row.hasLiquidity || selectedLiquidity,
        };
      });

      const newBalance = data.map((item) => {
        if (item.operationType.toLowerCase() === 'venta') {
          return {
            ...item,
            colorOperationType: palette.error['100'],
          };
        }
        if (item.operationType.toLowerCase() === 'compra') {
          return {
            ...item,
            colorOperationType: palette.success['100'],
          };
        }
        return {
          ...item,
          colorOperationType: 'transparent',
        };
      });

      balance.balancing = newBalance as Balancing[];

      // Normalized data
      balance.normalizedData = {
        ...data.reduce(assignBy('idRebalance'), {}),
        ...balance.normalizedData,
      };

      // Set total
      balance.total = action.payload.pagination.total;
    },

    setOperationQuery: (balance: BalanceStoreType) => {
      balance.operationQuery = balance.filters;
      balance.specialOperationQuery = balance.specialFilters;
    },

    selectedBalance: (
      balance: BalanceStoreType,
      action: PayloadAction<{ row: Record<string, any>; checked: boolean }>,
    ) => {
      let itemNotLiquidity: any = null;
      const newBalance = balance.balancing.map((item) => {
        if (action.payload.row.idRebalance === item.idRebalance) {
          if (!item.hasLiquidity) {
            itemNotLiquidity = item;
          }
          return {
            ...item,
            selected: action.payload.checked,
          };
        }
        return item;
      });

      balance.balancing = newBalance;

      if (itemNotLiquidity && action.payload.checked) {
        balance.selectedNotLiquidity = [
          ...balance.selectedNotLiquidity,
          itemNotLiquidity,
        ];
      } else if (itemNotLiquidity) {
        balance.selectedNotLiquidity = balance.selectedNotLiquidity.map(
          (item) => item.idRebalance !== itemNotLiquidity.idRebalance,
        );
      }

      // Normalized data
      balance.normalizedData[action.payload.row.idRebalance].selected =
        action.payload.checked;
    },

    setTotal: (balance: BalanceStoreType, action: PayloadAction<number>) => {
      balance.total = action.payload;
    },

    setPage: (balance: BalanceStoreType, action: PayloadAction<number>) => {
      balance.page = action.payload;
    },
    setPageSize: (balance: BalanceStoreType, action: PayloadAction<number>) => {
      balance.pageSize = action.payload;
    },
    resetPage: (balance: BalanceStoreType) => {
      balance.balancing = [];
      balance.normalizedData = [];
      balance.page = 1;
      balance.pageSize = 50;
    },

    setFilterValues: (
      balance: BalanceStoreType,
      action: PayloadAction<any>,
    ) => {
      const getValues = (
        data: Array<any>,
        param = '',
        filterType: string,
        returnParam = '',
      ) => {
        const arrayQuery: Array<any> = [];
        let resultObject: any = {};
        let valuesToSend = '';
        if (_.isEmpty(data)) return '';
        const values =
          balance.dataQuery[filterType as any as keyof BalanceDataQueryType];

        if (Array.isArray(values)) {
          values.forEach((oneData, index) => {
            // eslint-disable-next-line
            resultObject = data.find(
              (value) =>
                value[param] ===
                balance.dataQuery[filterType as keyof BalanceDataQueryType][
                  index
                ][param],
            );
            // Filter undefined values
            arrayQuery.push(
              resultObject === undefined
                ? balance.dataQuery[filterType as keyof BalanceDataQueryType][
                    index
                  ]
                : resultObject,
            );
          });
        }

        valuesToSend = arrayQuery
          .map((value) => (returnParam !== '' ? value[returnParam] : value))
          .join();

        return valuesToSend;
      };

      const { values, filterType, optionShow, returnParam } = action.payload;
      balance.filters = {
        ...balance.filters,
        [filterType]: getValues(values, optionShow, filterType, returnParam),
      };
    },
    setFilterValue: (balance: BalanceStoreType, action: PayloadAction<any>) => {
      const { value, filterType } = action.payload;
      balance.filters = {
        ...balance.filters,
        [filterType]: value,
      };
    },

    setSendFilters: (
      balance: BalanceStoreType,
      action: PayloadAction<boolean>,
    ) => {
      balance.filtersObject = action.payload;
    },

    cleanInputs: (
      balance: BalanceStoreType,
      action: PayloadAction<boolean>,
    ) => {
      balance.filters = {
        client: '',
        marketType: '',
        instrument: '',
        symbol: '',
        portfolio: '',
        strategy: '',
        institute: '',
        account: '',
        advisor: '',
        includeZeroTitles: false,
      };
      balance.dataQuery = normalQueryValues;
      balance.cleanFilters = action.payload;
      balance.notSelected = [];
    },
    cleanInputsSpecials: (balance: BalanceStoreType) => {
      balance.specialFilters = {
        specialOperation: '',
        virtualIssuer: 'Emisora Virtual',
        virtualPurchasePercentage: '',
        virtualPrice: '',
        virtualSalePercentage: '',
        includeValueStrategy: true,
        excludeValueStrategy: false,
      };
      balance.specialDataQuery = { ...specialQueryValues };
    },
    setOpenTooltip: (
      balance: BalanceStoreType,
      action: PayloadAction<boolean>,
    ) => {
      balance.showTooltip = action.payload;
    },

    setOperationType: (
      balance: BalanceStoreType,
      action: PayloadAction<string>,
    ) => {
      balance.operationType = action.payload;
    },

    setOpenFilter: (
      balance: BalanceStoreType,
      action: PayloadAction<{ name: string }>,
    ) => {
      balance.openFilter = {
        ...balance.openFilter,
        [action.payload.name]: !balance.openFilter[action.payload.name],
      };
    },

    closeAllFilters: (balance: BalanceStoreType) => {
      balance.openFilter = {
        marketType: false,
        instrument: false,
        symbol: false,
        portfolio: false,
        strategy: false,
        institute: false,
        account: false,
        advisor: false,
        client: false,
      };
      balance.specialOpenFilter = {
        specialOperation: false,
        virtualIssuer: false,
        virtualPurchasePercentage: false,
        virtualPrice: false,
        virtualSalePercentage: false,
        excludeValueStrategy: false,
        includeValueStrategy: false,
      };
      balance.isSubmitting = false;
    },

    setDataQuery: (balance: BalanceStoreType, action) => {
      balance.dataQuery = {
        ...balance.dataQuery,
        [action.payload.name]: action.payload.value,
      };
    },

    setSpecialDataQuery: (balance: BalanceStoreType, action) => {
      balance.specialDataQuery = {
        ...balance.specialDataQuery,
        [action.payload.name]: action.payload.value,
      };
    },

    setSpecialFilterValue: (balance: BalanceStoreType, action) => {
      const { value, filterType } = action.payload;
      balance.specialFilters = {
        ...balance.specialFilters,
        [filterType]: value,
      };
    },

    setSpecialOpenFilter: (
      balance: BalanceStoreType,
      action: PayloadAction<{ name: string }>,
    ) => {
      balance.specialOpenFilter = {
        ...balance.specialOpenFilter,
        [action.payload.name]: !balance.specialOpenFilter[action.payload.name],
      };
    },

    cleanDataQuery: (balance: BalanceStoreType) => {
      return {
        ...initialState,
        globalOrdersFilters: balance.globalOrdersFilters,
      };
    },

    setComplete: (
      balance: BalanceStoreType,
      action: PayloadAction<boolean>,
    ) => {
      balance.complete = action.payload;
    },

    setSelected: (
      balance: BalanceStoreType,
      action: PayloadAction<Array<any>>,
    ) => {
      balance.selected = action.payload;
    },

    setLoading: (
      balance: BalanceStoreType,
      { payload }: PayloadAction<boolean>,
    ) => {
      balance.loading = payload;
    },
    setSubmit: (
      balance: BalanceStoreType,
      { payload }: PayloadAction<boolean>,
    ) => {
      balance.isSubmitting = payload;
    },
    resetSelectedNotLiquidity: (balance: BalanceStoreType) => {
      balance.selectedNotLiquidity = [];
    },
  },
});

export const {
  setShowFilterSidebar,
  setBalancingData,
  selectedBalance,
  setTotal,
  setPage,
  setPageSize,
  resetPage,
  // setFilters,
  setFilterValues,
  setFilterValue,
  setSendFilters,
  cleanInputs,
  cleanInputsSpecials,
  setOpenTooltip,
  setOperationType,
  setOpenFilter,
  closeAllFilters,
  setDataQuery,
  setSpecialDataQuery,
  setSpecialFilterValue,
  setSpecialOpenFilter,
  cleanDataQuery,
  setOperationQuery,
  setComplete,
  setSelected,
  setLoading,
  setSubmit,
  resetSelectedNotLiquidity,
  setGlobalOrdersFilters,
  setBalancingSpecialOperation,
} = slice.actions;
export default slice.reducer;

// Selectors
export const getBalancing = (state: RootState) =>
  state.entities.balance.balancing;
export const getLoading = (state: RootState) => state.entities.balance.loading;
export const getSubmit = (state: RootState) =>
  state.entities.balance.isSubmitting;
export const getComplete = (state: RootState) =>
  state.entities.balance.complete;
export const getSelected = (state: RootState) =>
  state.entities.balance.selected;
export const getTotal = (state: RootState) => state.entities.balance.total;
export const getPage = (state: RootState) => state.entities.balance.page;
export const getPageSize = (state: RootState) =>
  state.entities.balance.pageSize;
export const getShowTooltip = (state: RootState) =>
  state.entities.balance.showTooltip;
export const getOperationType = (state: RootState) =>
  state.entities.balance.operationType;
export const getDataQuery = (state: RootState) =>
  state.entities.balance.dataQuery;
export const getOpenFilter = (state: RootState) =>
  state.entities.balance.openFilter;
export const getCleanFilters = (state: RootState) =>
  state.entities.balance.cleanFilters;
export const getFilters = (state: RootState) => state.entities.balance.filters;
export const getFiltersObject = (state: RootState) =>
  state.entities.balance.filtersObject;
export const getGlobalOrdersFilters = (state: RootState) =>
  state.entities.balance.globalOrdersFilters;
export const getFilterOpened = (state: RootState) =>
  state.entities.balance.filterOpened;
export const getNotSelected = (state: RootState) =>
  state.entities.balance.notSelected;
export const getOperationQuery = (state: RootState) =>
  state.entities.balance.operationQuery;
export const getNormalizedData = (state: RootState) =>
  state.entities.balance.normalizedData;
export const getBalancingSpecialOperation = (state: RootState) =>
  state.entities.balance.balancingSpecialOperation;

// Selectores para filtros especiales
export const getSpecialFilters = (state: RootState) =>
  state.entities.balance.specialFilters;
export const getSpecialDataQuery = (state: RootState) =>
  state.entities.balance.specialDataQuery;
export const getSpecialOpenFilter = (state: RootState) =>
  state.entities.balance.specialOpenFilter;
export const getSpecialOperationQuery = (state: RootState) =>
  state.entities.balance.specialOperationQuery;
